import { Component } from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router, RouterLink } from "@angular/router";
import { TranslatePipe } from "@ngx-translate/core";
import { GigyaService } from "../../services/gigya.service";
import { PasswordService } from "../../services/password.service";
import { InputComponent } from "../../shared/input/input.component";
import { ButtonComponent } from "../../shared/button/button.component";
import { SpinnerComponent } from "../../shared/icons/spinner/spinner.component";
import { LayoutComponent } from "../../shared/layout/layout.component";
import { WarningIconComponent } from "../../shared/icons/warning/warning.component";
import { TitleComponent } from "../../shared/title/title.component";
import { StepperComponent } from "../../shared/stepper/stepper.component";
import { BreadcrumbComponent } from "../../shared/breadcrumb/breadcrumb.component";
import { PasswordRequirementsComponent } from "../../shared/password-requirements/password-requirements.component";

@Component({
  selector: "app-register",
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
    InputComponent,
    ButtonComponent,
    SpinnerComponent,
    LayoutComponent,
    WarningIconComponent,
    TitleComponent,
    BreadcrumbComponent,
    StepperComponent,
    PasswordRequirementsComponent,
  ],
  templateUrl: "./register.component.html",
  styleUrl: "./register.component.css",
})
export class RegisterComponent {
  error: string | null = null;
  loading = false;
  form: FormGroup;

  validations = {
    minLength: false,
    uppercase: false,
    number: false,
    specialChar: false,
  };

  constructor(
    private router: Router,
    private gigyaService: GigyaService,
    private passwordService: PasswordService,
  ) {
    this.form = new FormGroup({
      email: new FormControl("", [Validators.required, Validators.email]),
      confirmEmail: new FormControl("", [Validators.required, Validators.email]),
      password: new FormControl("", [Validators.required, this.passwordService.validator()]),
      confirmPassword: new FormControl("", [Validators.required]),
      terms: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  get emailMismatch(): boolean {
    const email = this.form.get("email")?.value;
    const confirmEmail = this.form.get("confirmEmail")?.value;
    return email && confirmEmail && email !== confirmEmail;
  }

  get passwordMismatch(): boolean {
    const password = this.form.get("password")?.value;
    const confirmPassword = this.form.get("confirmPassword")?.value;
    return password && confirmPassword && password !== confirmPassword;
  }

  checkPassword(): void {
    const control = this.form.get("password");
    this.validations = this.passwordService.checkStrength(control?.value || "");
    control?.updateValueAndValidity({ emitEvent: false });
  }

  public async onSubmit(): Promise<void> {
    if (!this.form.valid || this.emailMismatch || this.passwordMismatch) return;

    this.error = null;
    this.loading = true;

    const { email, password } = this.form.value;

    const initRes = await this.gigyaService.execute(this.gigyaService.client.accounts.initRegistration, {});

    if (initRes.errorCode !== 0) {
      this.loading = false;
      this.error = initRes['errorMessage'];
      return;
    }

    const res = await this.gigyaService.execute(this.gigyaService.client.accounts.register, {
      email,
      password,
      regToken: initRes.regToken,
      finalizeRegistration: true,
    });

    this.loading = false;
    // 0 = success, 206002 = pending email verification (account created)
    if (res.errorCode === 0 || res.errorCode === 206002) {
      this.router.navigate(["/register-email-sent"], { queryParams: { email } });
      return;
    }
    this.error = res['errorMessage'];
  }
}
